// src/pages/financeiro/AssinaturaConsultaPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { FaCheck, FaClock, FaSave, FaTimes, FaTrashAlt } from 'react-icons/fa';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ClienteSearch from '../pessoas/ClienteSearch';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { useAssinatura } from '../../contexts/financeiro/AssinaturaContext';
import { statusClienteOptions } from '../../models/financeiro/Assinatura';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate, formatDateInput } from '../../utils/dateTimeUtils';
import './Recebimento.css';
import './AssinaturaConsultaPage.css';

const getDateFromInput = (value) => {
  if (!value) {
    return new Date();
  }

  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getUltimoDiaMes = (value = new Date()) => {
  const data = value instanceof Date ? value : getDateFromInput(value);
  return new Date(data.getFullYear(), data.getMonth() + 1, 0);
};

const AssinaturaConsultaPage = () => {
  const { buscarFaturas, lancarPagamento, adiarBloqueio } = useAssinatura();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();
  const [faturas, setFaturas] = useState([]);
  const [selectedFatura, setSelectedFatura] = useState(null);
  const [showSearchModalCliente, setShowSearchModalCliente] = useState(false);
  const [clienteId, setClienteId] = useState(null);
  const [clienteNome, setClienteNome] = useState('');
  const [periodoInicial, setPeriodoInicial] = useState('');
  const [periodoFinal, setPeriodoFinal] = useState('');
  const [statusCliente, setStatusCliente] = useState('');
  const [situacao, setSituacao] = useState('todos');
  const [dataPagamento, setDataPagamento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [showAdiarBloqueioModal, setShowAdiarBloqueioModal] = useState(false);
  const [qtdDiasAdiarBloqueio, setQtdDiasAdiarBloqueio] = useState('');

  const totais = useMemo(() => {
    return faturas.reduce((acc, item) => {
      if (item.pago) {
        acc.recebido += item.valor || 0;
        acc.qtdRecebida += 1;
      } else {
        acc.emAberto += item.valor || 0;
        acc.qtdAberta += 1;
      }

      return acc;
    }, { recebido: 0, emAberto: 0, qtdRecebida: 0, qtdAberta: 0 });
  }, [faturas]);

  useEffect(() => {
    const dataAtual = new Date();
    const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
    setPeriodoInicial(formatDateInput(primeiroDiaMes));
    setPeriodoFinal(formatDateInput(getUltimoDiaMes(dataAtual)));
  }, []);

  useEffect(() => {
    if (periodoInicial && periodoFinal) {
      buscar();
    }
  }, [periodoInicial, periodoFinal]);

  const handleSelectCliente = (clienteSel) => {
    setShowSearchModalCliente(false);
    if (!clienteSel) return;

    setClienteId(clienteSel.id);
    setClienteNome(clienteSel.pessoa?.nome || '');
  };

  const handlePeriodoFinalChange = (value) => {
    setPeriodoFinal(value ? formatDateInput(getUltimoDiaMes(value)) : '');
  };

  const buscar = async () => {
    const filtro = {
      clienteId,
      clienteNome,
      statusCliente: statusCliente === '' ? null : Number(statusCliente),
      dataIni: periodoInicial ? `${periodoInicial}T00:00:00` : null,
      dataFim: periodoFinal ? `${periodoFinal}T23:59:59` : null,
      pago: situacao === 'todos' || situacao === 'atrasadas' ? null : situacao === 'pagas',
      somenteAtrasadas: situacao === 'atrasadas',
    };

    try {
      const result = await buscarFaturas(filtro);
      setFaturas(result || []);
      setSelectedFatura(null);
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      showMessage(`Erro ao buscar faturas: ${error?.message || error}`, 'danger');
      setFaturas([]);
    }
  };

  const limparCliente = () => {
    setClienteId(null);
    setClienteNome('');
  };

  const selecionarFatura = (item) => {
    setSelectedFatura(item);
    setDataPagamento(item.dataPagamento ? formatDateInput(item.dataPagamento) : formatDateInput(new Date()));
    setObservacao(item.observacao || '');
  };

  const lancar = async (pago) => {
    if (!selectedFatura) {
      showMessage('Selecione uma fatura para lançar.', 'warning');
      return;
    }

    await lancarPagamento({
      faturaId: selectedFatura.faturaId,
      pago,
      dataPagamento: pago && dataPagamento ? `${dataPagamento}T00:00:00` : null,
      observacao,
    });

    await buscar();
  };

  const removerPagamento = async () => {
    if (!selectedFatura) {
      showMessage('Selecione uma fatura para remover o pagamento.', 'warning');
      return;
    }

    if (!selectedFatura.pago) {
      showMessage('A fatura selecionada não está paga.', 'warning');
      return;
    }

    const resposta = await showYesNo('Deseja realmente remover o pagamento?');
    if (!resposta) return;

    await lancar(false);
  };

  const abrirModalAdiarBloqueio = () => {
    if (!selectedFatura) {
      showMessage('Selecione uma fatura para adiar o pagamento.', 'warning');
      return;
    }

    setQtdDiasAdiarBloqueio(selectedFatura.adiarBloqueio || '');
    setShowAdiarBloqueioModal(true);
  };

  const salvarAdiarBloqueio = async () => {
    const qtdDias = Number(qtdDiasAdiarBloqueio || 0);

    if (qtdDias <= 0) {
      showMessage('Informe a quantidade de dias para adiar o pagamento.', 'warning');
      return;
    }

    await adiarBloqueio({
      assinaturaFaturaId: selectedFatura.faturaId,
      qtdDias,
    });

    setShowAdiarBloqueioModal(false);
    await buscar();
  };
  return (
    <PageContainer>
      <div className="assinatura-consulta-page">
        <h2 className="page-title">Consulta de Faturas</h2>

        <PageHeader>
          <div className="assinatura-consulta-filtros">
            <Input label="Cliente" value={clienteNome} size={220} onChange={(e) => setClienteNome(e.target.value)} btnBuscar={() => setShowSearchModalCliente(true)} />
            <button type="button" className="btn btn-secondary assinatura-consulta-limpar" onClick={limparCliente}>Limpar</button>
            <Select
              label="Status Cliente"
              value={statusCliente}
              options={[{ value: '', label: 'Todos' }, ...statusClienteOptions]}
              size={140}
              onChange={(e) => setStatusCliente(e.target.value)}
            />
            <Select
              label="Situação"
              value={situacao}
              options={[
                { value: 'todos', label: 'Todos' },
                { value: 'pendentes', label: 'Pendentes' },
                { value: 'pagas', label: 'Pagas' },
                { value: 'atrasadas', label: 'Atrasadas' },
              ]}
              size={130}
              onChange={(e) => setSituacao(e.target.value)}
            />
            <Input label="Data Inicial" type="date" size={140} value={periodoInicial} onChange={(e) => setPeriodoInicial(e.target.value)} />
            <Input label="Data Final" type="date" size={140} value={periodoFinal} onChange={(e) => handlePeriodoFinalChange(e.target.value)} />
            <button type="button" className="btn btn-search assinatura-consulta-buscar" onClick={buscar}>Buscar</button>
          </div>
        </PageHeader>

        <PageContent>
          <table className="page-content-table assinatura-consulta-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Cliente</th>
                <th>Descrição</th>
                <th>Competência</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Pix Enviado</th>
                <th>Data Pag.</th>
                <th>Status</th>
                <th>Dias Atraso</th>
                <th>Obs</th>
              </tr>
            </thead>
            <tbody>
              {faturas.length > 0 ? (
                faturas.map((item) => (
                  <tr
                    key={item.faturaId}
                    onDoubleClick={() => selecionarFatura(item)}
                    className={`${item.pago ? 'fatura-paga' : item.diasAtraso > 0 ? 'atraso' : ''} ${selectedFatura?.faturaId === item.faturaId ? 'selected' : ''}`}
                  >
                    <td data-label="Selecionar">
                      <input
                        type="radio"
                        checked={selectedFatura?.faturaId === item.faturaId}
                        onChange={() => selecionarFatura(item)}
                      />
                    </td>
                    <td data-label="ID">{item.faturaId}</td>
                    <td data-label="Cliente">{item.clienteNome || '-'}</td>
                    <td data-label="Descrição">{item.descricao || '-'}</td>
                    <td data-label="Competência">{item.competencia}</td>
                    <td data-label="Vencimento">{formatDate(item.dataVencimento)}</td>
                    <td data-label="Valor">{formatCurrency(item.valor)}</td>
                    <td data-label="Pix Enviado">{item.pixEnviado ? 'Sim' : 'Não'}</td>
                    <td data-label="Data Pag.">{item.dataPagamento ? formatDate(item.dataPagamento) : '-'}</td>
                    <td data-label="Status">{item.pago ? 'Pago' : 'Pendente'}</td>
                    <td data-label="Dias Atraso">{item.diasAtraso}</td>
                    <td data-label="Obs">{item.observacao || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12}>Nenhuma fatura encontrada.</td>
                </tr>
              )}
            </tbody>
          </table>
        </PageContent>

        <PageFooter>
          <div className="botoes-footer">
            <Input label="Data Pagamento" type="date" size={150} value={dataPagamento} onChange={(e) => setDataPagamento(e.target.value)} />
            <Input label="Observação" size={260} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
            <button className="btn btn-success btn-footer" type="button" onClick={() => lancar(true)}><FaCheck className="me-1" />Lançar Pago</button>
            {selectedFatura?.pago && (
              <button className="btn btn-danger btn-footer" type="button" onClick={removerPagamento}><FaTrashAlt className="me-1" />Remover Pagamento</button>
            )}
            <button className="btn btn-warning btn-footer" type="button" onClick={abrirModalAdiarBloqueio}><FaClock className="me-1" />Adiar Pagamento</button>
          </div>
          <div className="totais-footer">
            <strong>Recebido:</strong> {formatCurrency(totais.recebido)} ({totais.qtdRecebida})
            <strong>Em Aberto:</strong> {formatCurrency(totais.emAberto)} ({totais.qtdAberta})
          </div>
        </PageFooter>

        <ModalContainer show={showAdiarBloqueioModal} largura="560px">
          <ModalHeader>
            <h5 className="modal-title">Adiar Pagamento</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowAdiarBloqueioModal(false)} />
          </ModalHeader>
          <ModalBody>
            <div className="input-group mb-3">
              <Input label="Vencimento" value={selectedFatura ? formatDate(selectedFatura.dataVencimento) : ''} size={140} disabled />
              <Input label="Valor" value={selectedFatura ? formatCurrency(selectedFatura.valor) : ''} size={140} disabled />
              <Input label="Dias em Atraso" value={selectedFatura?.diasAtraso || 0} size={120} disabled />
              <Input
                label="Qtd. Dias"
                type="number"
                value={qtdDiasAdiarBloqueio}
                size={120}
                onChange={(e) => setQtdDiasAdiarBloqueio(e.target.value)}
                autoFocus
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="btn-group">
              <button type="button" className="btn btn-success" onClick={salvarAdiarBloqueio}><FaSave className="me-1" />Salvar</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAdiarBloqueioModal(false)}><FaTimes className="me-1" />Cancelar</button>
            </div>
          </ModalFooter>
        </ModalContainer>

        <ClienteSearch
          show={showSearchModalCliente}
          onHide={() => setShowSearchModalCliente(false)}
          onSelectCliente={handleSelectCliente}
        />
      </div>
    </PageContainer>
  );
};

export default AssinaturaConsultaPage;
